import { DiagnosticsPage } from '@/app/pages/Diagnostics/DiagnosticsPage'
import {
	ActionSettingsPage,
	ApiKeysSettingsPage,
	AuditLogsPage,
	MetadataPage,
	ProjectGeneralPage,
	QuotasPage,
	RoleSettingsPage,
	SettingsPage,
	StreamsPage,
	SubscriberGroupsPage,
	UsersSettingsPage,
} from '@/app/routes/lazyPages'
import { apiTypeSchema, tabAllOnlySchema, tabAnySchema } from '@/app/routes/params'
import { organizationProjectRoute } from '@/app/routes/projectRoutes'
import { RouteAccessBoundary } from '@/components/access-control'
import { authenticatedRouteGuard, projectSettingsAdminRouteGuard } from '@/lib/routeGuards'
import { streamsSearchSchema } from '@/lib/searchSchemas'
import { Outlet, createRoute, redirect } from '@tanstack/react-router'

/**
 * The project settings subtree, `/orgs/$orgId/projects/$tenantId/settings`.
 *
 * Admin gating is per tab rather than on the parent: general, users, roles, quotas, audit logs and API
 * keys are admin only, while metadata, streams, action settings and subscriber groups stay open to
 * non-admins and restrict their own bodies through RBAC.
 */
export const settingsRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/settings',
	component: () => <SettingsPage />,
	// Admin gating is per-tab below (general/users/roles/quotas/audit-logs/api-keys); the
	// RBAC-gated tabs (metadata/streams/action-settings/subscriber-groups) stay open to non-admins.
	beforeLoad: ({ context }) => {
		authenticatedRouteGuard(context)
	},
})

// Index route for settings that redirects to general
export const settingsIndexRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/settings/general',
			params,
			search,
			replace: true,
		})
	},
})

export const generalSettingsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/general',
	component: () => (
		<RouteAccessBoundary resource='project_settings'>
			<ProjectGeneralPage />
		</RouteAccessBoundary>
	),
	beforeLoad: ({ context, params }) => {
		projectSettingsAdminRouteGuard(context, params)
	},
})

export const ApiKeysSettingsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/api-keys',
	component: () => <Outlet />,
	beforeLoad: ({ context, params }) => {
		projectSettingsAdminRouteGuard(context, params)
	},
})
export const settingsApiKeysIndexRoute = createRoute({
	getParentRoute: () => ApiKeysSettingsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/settings/api-keys/$type',
			params: { ...params, type: 'all' },
			search,
			replace: true,
		})
	},
})
export const settingsApiKeysTypeRoute = createRoute({
	getParentRoute: () => ApiKeysSettingsRoute,
	path: '/$type',
	parseParams: params => apiTypeSchema.parse(params),
	component: () => <ApiKeysSettingsPage />,
})

export const UsersSettingsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/users',
	component: () => <Outlet />,
	beforeLoad: ({ context, params }) => {
		projectSettingsAdminRouteGuard(context, params)
	},
})
export const settingsUsersIndexRoute = createRoute({
	getParentRoute: () => UsersSettingsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/settings/users/$type',
			params: { ...params, type: 'all' },
			search,
			replace: true,
		})
	},
})
// Add users with type parameter:
export const settingsUsersTypeRoute = createRoute({
	getParentRoute: () => UsersSettingsRoute,
	path: '/$type',
	parseParams: params => tabAnySchema.parse(params),
	component: () => <UsersSettingsPage />,
})

export const RoleSettingsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/roles',
	component: () => <Outlet />,
	beforeLoad: ({ context, params }) => {
		projectSettingsAdminRouteGuard(context, params)
	},
})
export const settingsRoleIndexRoute = createRoute({
	getParentRoute: () => RoleSettingsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/settings/roles/$type',
			params: { ...params, type: 'all' },
			search,
			replace: true,
		})
	},
})
export const settingsRoleTypeRoute = createRoute({
	getParentRoute: () => RoleSettingsRoute,
	path: '/$type',
	parseParams: params => tabAllOnlySchema.parse(params),
	component: () => <RoleSettingsPage />,
})

export const QuotasSettingsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/quotas',
	component: () => (
		<RouteAccessBoundary resource='project_settings'>
			<QuotasPage />
		</RouteAccessBoundary>
	),
	beforeLoad: ({ context, params }) => {
		projectSettingsAdminRouteGuard(context, params)
	},
})

// SETTINGS — Metadata (moved from Data section)
export const settingsMetadataRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/metadata',
	// No route-level boundary: MetadataPage stays mounted so its header is kept; the body
	// self-restricts via per-resource RBAC. See MetadataPage.
	component: () => <Outlet />,
})
export const settingsMetadataIndexRoute = createRoute({
	getParentRoute: () => settingsMetadataRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/settings/metadata/$type',
			params: { ...params, type: 'all' },
			search,
			replace: true,
		})
	},
})
export const settingsMetadataTypeRoute = createRoute({
	getParentRoute: () => settingsMetadataRoute,
	path: '/$type',
	parseParams: params => tabAllOnlySchema.parse(params),
	component: () => <MetadataPage />,
})

// SETTINGS — Streams (moved from Data section)
export const settingsStreamsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/streams',
	// No route-level boundary: StreamsPage stays mounted so its header is kept; the body
	// self-restricts via per-resource RBAC. See StreamsPage.
	component: () => <Outlet />,
})
export const settingsStreamsIndexRoute = createRoute({
	getParentRoute: () => settingsStreamsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/settings/streams/$type',
			params: { ...params, type: 'all' },
			search,
			replace: true,
		})
	},
})
export const settingsStreamsTypeRoute = createRoute({
	getParentRoute: () => settingsStreamsRoute,
	path: '/$type',
	parseParams: params => tabAllOnlySchema.parse(params),
	validateSearch: streamsSearchSchema,
	component: () => <StreamsPage />,
})

// SETTINGS — Action Settings (moved from Actions section)
export const settingsActionSettingsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/action-settings',
	// No route-level boundary: ActionSettingsPage stays mounted so its header is kept, and the body
	// self-restricts via per-resource RBAC. See ActionSettingsPage.
	component: () => <Outlet />,
})
export const settingsActionSettingsIndexRoute = createRoute({
	getParentRoute: () => settingsActionSettingsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/settings/action-settings/$type',
			params: { ...params, type: 'active' },
			search,
			replace: true,
		})
	},
})
export const settingsActionSettingsTypeRoute = createRoute({
	getParentRoute: () => settingsActionSettingsRoute,
	path: '/$type',
	component: () => <ActionSettingsPage />,
})

// SETTINGS — Subscriber Groups (moved from Sessions section)
export const settingsSubscriberGroupsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/subscriber-groups',
	// No route-level boundary: SubscriberGroupsPage stays mounted so its header is kept; the body
	// self-restricts via per-resource RBAC. See SubscriberGroupsPage.
	component: () => <Outlet />,
})
export const settingsSubscriberGroupsIndexRoute = createRoute({
	getParentRoute: () => settingsSubscriberGroupsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/settings/subscriber-groups/$type',
			params: { ...params, type: 'all' },
			search,
			replace: true,
		})
	},
})
export const settingsSubscriberGroupsTypeRoute = createRoute({
	getParentRoute: () => settingsSubscriberGroupsRoute,
	path: '/$type',
	component: () => <SubscriberGroupsPage />,
})

export const diagnosticsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/diagnostics',
	component: () => <DiagnosticsPage />,
	beforeLoad: ({ context }) => {
		authenticatedRouteGuard(context)
	},
})

export const auditLogsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/audit-logs',
	component: () => <Outlet />,
	beforeLoad: ({ context, params }) => {
		projectSettingsAdminRouteGuard(context, params)
	},
})

// Index route for audit logs that redirects to all
export const auditLogsIndexRoute = createRoute({
	getParentRoute: () => auditLogsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/settings/audit-logs/$type',
			params: { ...params, type: 'all' },
			search,
			replace: true,
		})
	},
})

// Audit logs with type parameter
export const auditLogsTypeRoute = createRoute({
	getParentRoute: () => auditLogsRoute,
	path: '/$type',
	parseParams: params => tabAllOnlySchema.parse(params),
	component: () => <AuditLogsPage />,
	beforeLoad: ({ context, location }) => {
		authenticatedRouteGuard(context, { location })
	},
})

/** The settings subtree, assembled so the composition root nests sections rather than leaf routes. */
export const projectSettingsRouteTree = settingsRoute.addChildren([
	settingsIndexRoute,
	generalSettingsRoute,
	UsersSettingsRoute.addChildren([settingsUsersIndexRoute, settingsUsersTypeRoute]),
	RoleSettingsRoute.addChildren([settingsRoleIndexRoute, settingsRoleTypeRoute]),
	ApiKeysSettingsRoute.addChildren([settingsApiKeysIndexRoute, settingsApiKeysTypeRoute]),
	QuotasSettingsRoute,
	diagnosticsRoute,
	settingsMetadataRoute.addChildren([settingsMetadataIndexRoute, settingsMetadataTypeRoute]),
	settingsStreamsRoute.addChildren([settingsStreamsIndexRoute, settingsStreamsTypeRoute]),
	settingsActionSettingsRoute.addChildren([settingsActionSettingsIndexRoute, settingsActionSettingsTypeRoute]),
	settingsSubscriberGroupsRoute.addChildren([settingsSubscriberGroupsIndexRoute, settingsSubscriberGroupsTypeRoute]),
	auditLogsRoute.addChildren([auditLogsIndexRoute, auditLogsTypeRoute]),
])
